import React, { useEffect, useState, useMemo } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Heart, Loader2, Star, SlidersHorizontal, X, ChevronDown, ChevronUp,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { useCart } from "../../../utils/CartContext";
import { useCurrency } from "../../../context/CurrencyContext";
import { formatingPrice } from "../../../utils/formatingPrice";
import { useTranslation } from "react-i18next";
import GuestSession from "../../../utils/guestSession";

// ─── FilterSection accordion ───────────────────────────────────────────────
const FilterSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 py-3">
      <button
        onClick={() => setOpen(!open)}
        className="flex justify-between items-center w-full text-left"
      >
        <span className="font-semibold text-gray-800 text-sm">{title}</span>
        {open
          ? <ChevronUp className="w-4 h-4 text-gray-500" />
          : <ChevronDown className="w-4 h-4 text-gray-500" />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
};

//  Main Component 
const SpecialProducts = ({
  products = [],
  subcategories = [],
  selectedSubCategoryFromParent,
  onSubCategoryChange,
}) => {
  const { currency, convertPrice } = useCurrency();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";

  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loadingHearts, setLoadingHearts] = useState(new Set());
  const [loadingCart, setLoadingCart] = useState(new Set());
  const { fetchCartCount } = useCart();

  // ── Filter state ──
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100000]);
  const [selectedMaterials, setSelectedMaterials] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedHeights, setSelectedHeights] = useState([]);
  const [selectedWeights, setSelectedWeights] = useState([]);
  const [selectedMeasurements, setSelectedMeasurements] = useState([]);

  const storedUser = localStorage.getItem("pm-user-data");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?._id;
  const isLoggedIn = !!userId;

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 100 });
    fetchWishlist();
  }, [userId]);

  // Listen for guest wishlist updates
  useEffect(() => {
    const handleGuestWishlistUpdate = () => {
      if (!isLoggedIn) {
        const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
        setWishlist(guestWishlist);
      }
    };
    window.addEventListener('guest-wishlist-updated', handleGuestWishlistUpdate);
    return () => window.removeEventListener('guest-wishlist-updated', handleGuestWishlistUpdate);
  }, [isLoggedIn]);

  useEffect(() => {
    if (selectedSubCategoryFromParent !== undefined) {
      setSelectedSubCategory(selectedSubCategoryFromParent);
    }
  }, [selectedSubCategoryFromParent]);

  // ── DEFINE getLocalizedText FIRST before any useMemo that uses it ──
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    if (typeof item[field] === "string") return item[field];
    if (typeof item[field] === "object") {
      return (
        item[field][currentLanguage] ||
        item[field].en ||
        item[field].hi ||
        item[field].es ||
        item[field].zh ||
        ""
      );
    }
    return "";
  };

  // ── Derive price bounds ──
  const { minPrice, maxPrice } = useMemo(() => {
    const prices = products.map((p) => p.price || 0);
    return {
      minPrice: prices.length ? Math.min(...prices) : 0,
      maxPrice: prices.length ? Math.max(...prices) : 100000,
    };
  }, [products]);

  // ── Init price range when products load ──
  useEffect(() => {
    setPriceRange([minPrice, maxPrice]);
  }, [minPrice, maxPrice]);

  // ── Derive all unique filter options from products ──
  const allMaterials = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const m = getLocalizedText(p, "material");
      if (m) set.add(m);
    });
    return [...set];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, currentLanguage]);

  const allBrands = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.brand) set.add(p.brand);
    });
    return [...set];
  }, [products]);

  const allHeights = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.height) set.add(p.height);
    });
    return [...set];
  }, [products]);

  const allWeights = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      if (p.weight) set.add(p.weight);
    });
    return [...set];
  }, [products]);

  const allMeasurements = useMemo(() => {
    const set = new Set();
    products.forEach((p) => {
      const m = getLocalizedText(p, "measurement");
      if (m) set.add(m);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return [...set];
  }, [products, currentLanguage]);

  // ── Filtered products ──
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      // ── Subcategory filter ──
      if (selectedSubCategory) {
        const subId =
          p.subCategory?._id?.toString() ||
          p.subCategoryId?.toString() ||
          p.subCategory?.toString();
        if (subId !== selectedSubCategory) return false;
      }

      // ── Rating filter ──
      if (selectedRatings.length > 0) {
        const minRating = Math.min(...selectedRatings);
        if ((p.ratings || 0) < minRating) return false;
      }

      // ── Price filter ──
      if (p.price < priceRange[0] || p.price > priceRange[1]) return false;

      // ── Material filter ──
      if (selectedMaterials.length > 0) {
        const mat = getLocalizedText(p, "material");
        if (!selectedMaterials.includes(mat)) return false;
      }

      // ── Brand filter ──
      if (selectedBrands.length > 0) {
        if (!selectedBrands.includes(p.brand)) return false;
      }

      // ── Height filter ──
      if (selectedHeights.length > 0) {
        if (!selectedHeights.includes(p.height)) return false;
      }

      // ── Weight filter ──
      if (selectedWeights.length > 0) {
        if (!selectedWeights.includes(p.weight)) return false;
      }

      // ── Measurement filter ──
      if (selectedMeasurements.length > 0) {
        const meas = getLocalizedText(p, "measurement");
        if (!selectedMeasurements.includes(meas)) return false;
      }

      return true;
    });

  }, [
    products,
    selectedSubCategory,
    selectedRatings,
    priceRange,
    selectedMaterials,
    selectedBrands,
    selectedHeights,
    selectedWeights,
    selectedMeasurements,
  ]);

  const toggleArray = (arr, setArr, val) =>
    setArr(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);

  const handleSubCategorySelect = (id) => {
    setSelectedSubCategory(id);
    onSubCategoryChange?.(id); 
  };

  const resetFilters = () => {
    setSelectedSubCategory(null);
    setSelectedRatings([]);
    setPriceRange([minPrice, maxPrice]);
    setSelectedMaterials([]);
    setSelectedBrands([]);
    setSelectedHeights([]);
    setSelectedWeights([]);
    setSelectedMeasurements([]);
    onSubCategoryChange?.(null);
  };

  // ── Wishlist (works for both logged in and guest) ──
  const fetchWishlist = async () => {
    try {
      if (isLoggedIn) {
        // Logged in user - fetch from API
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/wishlist/wishlist-by-user/${userId}`
        );
        const ids = res?.data?.products?.map((p) => p._id?.toString()) || [];
        setWishlist(ids);
      } else {
        // Guest user - get from localStorage
        const guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
        setWishlist(guestWishlist);
      }
    } catch (error) {
      console.error("Error fetching wishlist:", error);
    }
  };

  const toggleWishlist = async (productId) => {
    const isLiked = wishlist.includes(productId.toString());
    setLoadingHearts((prev) => new Set(prev).add(productId));

    try {
      if (isLoggedIn) {
        // Logged in user
        const endpoint = isLiked
          ? `${process.env.REACT_APP_API_URL}/wishlist/remove-from-wishlist`
          : `${process.env.REACT_APP_API_URL}/wishlist/add-to-wishlist`;
        await axios.post(endpoint, { userId, productId });
        await fetchWishlist();
        toast.success(isLiked ? "Removed from wishlist" : "Added to wishlist");
      } else {
        // Guest user - use localStorage
        let guestWishlist = JSON.parse(localStorage.getItem('guestWishlist') || '[]');
        if (isLiked) {
          guestWishlist = guestWishlist.filter(id => id !== productId);
        } else {
          guestWishlist.push(productId);
        }
        localStorage.setItem('guestWishlist', JSON.stringify(guestWishlist));
        setWishlist(guestWishlist);
        window.dispatchEvent(new Event('guest-wishlist-updated'));
        toast.success(isLiked ? "Removed from wishlist" : "Added to wishlist");
      }
    } catch (error) {
      toast.error(t("something_went_wrong") || "Something went wrong!");
    } finally {
      setLoadingHearts((prev) => {
        const s = new Set(prev);
        s.delete(productId);
        return s;
      });
    }
  };

  // ── Add to Cart (works for both logged in and guest) ──
  const handleAddToCart = async (productId) => {
    setLoadingCart((prev) => new Set(prev).add(productId));

    try {
      if (isLoggedIn) {
        // Logged in user
        await axios.post(
          `${process.env.REACT_APP_API_URL}/cart/add-to-cart`,
          {
            userId,
            productId,
            quantity: 1,
          }
        );
      } else {
        // Guest user
        const guestId = GuestSession.getGuestId();
        await axios.post(
          `${process.env.REACT_APP_API_URL}/cart/add-to-cart`,
          {
            guestId,
            productId,
            quantity: 1,
          }
        );
      }
      toast.success(t("added_to_cart") || "Added to cart!");
      await fetchCartCount();
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error(error.response?.data?.message || t("add_to_cart_failed") || "Failed to add to cart");
    } finally {
      setLoadingCart((prev) => {
        const s = new Set(prev);
        s.delete(productId);
        return s;
      });
    }
  };

  const handleProductClick = (id) => navigate(`/product-details/${id}`);

  const renderStars = (rating) => (
    <div className="flex justify-center gap-0.5 mb-1">
      {[...Array(5)].map((_, i) => (
        <Star
          key={i}
          className={`w-3.5 h-3.5 ${
            i < Math.round(rating)
              ? "text-yellow-400 fill-yellow-400"
              : "text-gray-300"
          }`}
        />
      ))}
      {rating > 0 && (
        <span className="text-xs text-gray-500 ml-1">{rating.toFixed(1)}</span>
      )}
    </div>
  );

  //  Sidebar Filter Panel 
  const FilterPanel = () => (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-3">
        <span className="font-bold text-gray-900 text-base">Filter</span>
        <button
          onClick={resetFilters}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
        >
          <X className="w-3 h-3" /> Reset
        </button>
      </div>

      {/*  Subcategory  */}
      {subcategories.length > 0 && (
        <FilterSection title="Category">
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="radio"
                name="subcat"
                checked={!selectedSubCategory}
                onChange={() => handleSubCategorySelect(null)}
                className="accent-orange-500"
              />
              <span className="text-gray-700">All</span>
            </label>
            {subcategories.map((sub) => {
              const name = getLocalizedText(sub, "name");
              return (
                <label
                  key={sub._id}
                  className="flex items-center gap-2 cursor-pointer text-sm"
                >
                  <input
                    type="radio"
                    name="subcat"
                    checked={selectedSubCategory === sub._id}
                    onChange={() => handleSubCategorySelect(sub._id)}
                    className="accent-orange-500"
                  />
                  <span className="text-gray-700">{name}</span>
                </label>
              );
            })}
          </div>
        </FilterSection>
      )}

      {/*  Rating  */}
      <FilterSection title="Rating">
        <div className="space-y-2">
          {[4, 3, 2, 1].map((r) => (
            <label
              key={r}
              className="flex items-center gap-2 cursor-pointer text-sm"
            >
              <input
                type="checkbox"
                checked={selectedRatings.includes(r)}
                onChange={() =>
                  toggleArray(selectedRatings, setSelectedRatings, r)
                }
                className="accent-orange-500 rounded"
              />
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-3 h-3 ${
                      i < r
                        ? "text-yellow-400 fill-yellow-400"
                        : "text-gray-300"
                    }`}
                  />
                ))}
                <span className="text-gray-600 ml-1">{r}+</span>
              </div>
            </label>
          ))}
        </div>
      </FilterSection>

      {/*  Price Range  */}
      <FilterSection title="Price">
        <div className="space-y-3">
          <div className="flex justify-between text-xs text-gray-600">
            <span>₹{priceRange[0].toLocaleString()}</span>
            <span>₹{priceRange[1].toLocaleString()}</span>
          </div>
          <input
            type="range"
            min={minPrice}
            max={maxPrice}
            step={1}
            value={priceRange[0]}
            onChange={(e) =>
              setPriceRange([Number(e.target.value), priceRange[1]])
            }
            className="w-full accent-orange-500"
          />
          <input
            type="range"
            min={minPrice}
            max={maxPrice}
            step={1}
            value={priceRange[1]}
            onChange={(e) =>
              setPriceRange([priceRange[0], Number(e.target.value)])
            }
            className="w-full accent-orange-500"
          />
        </div>
      </FilterSection>

      {/*  Brand  */}
      {allBrands.length > 0 && (
        <FilterSection title="Brand">
          <div className="space-y-2">
            {allBrands.map((b) => (
              <label
                key={b}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedBrands.includes(b)}
                  onChange={() =>
                    toggleArray(selectedBrands, setSelectedBrands, b)
                  }
                  className="accent-orange-500 rounded"
                />
                <span className="text-gray-700">{b}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/*  Material  */}
      {allMaterials.length > 0 && (
        <FilterSection title="Material" defaultOpen={false}>
          <div className="space-y-2">
            {allMaterials.map((m) => (
              <label
                key={m}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedMaterials.includes(m)}
                  onChange={() =>
                    toggleArray(selectedMaterials, setSelectedMaterials, m)
                  }
                  className="accent-orange-500 rounded"
                />
                <span className="text-gray-700">{m}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/*  Height  */}
      {allHeights.length > 0 && (
        <FilterSection title="Height" defaultOpen={false}>
          <div className="space-y-2">
            {allHeights.map((h) => (
              <label
                key={h}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedHeights.includes(h)}
                  onChange={() =>
                    toggleArray(selectedHeights, setSelectedHeights, h)
                  }
                  className="accent-orange-500 rounded"
                />
                <span className="text-gray-700">{h}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/*  Weight  */}
      {allWeights.length > 0 && (
        <FilterSection title="Weight" defaultOpen={false}>
          <div className="space-y-2">
            {allWeights.map((w) => (
              <label
                key={w}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedWeights.includes(w)}
                  onChange={() =>
                    toggleArray(selectedWeights, setSelectedWeights, w)
                  }
                  className="accent-orange-500 rounded"
                />
                <span className="text-gray-700">{w}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}

      {/*  Measurement  */}
      {allMeasurements.length > 0 && (
        <FilterSection title="Measurement" defaultOpen={false}>
          <div className="space-y-2">
            {allMeasurements.map((m) => (
              <label
                key={m}
                className="flex items-center gap-2 cursor-pointer text-sm"
              >
                <input
                  type="checkbox"
                  checked={selectedMeasurements.includes(m)}
                  onChange={() =>
                    toggleArray(
                      selectedMeasurements,
                      setSelectedMeasurements,
                      m
                    )
                  }
                  className="accent-orange-500 rounded"
                />
                <span className="text-gray-700">{m}</span>
              </label>
            ))}
          </div>
        </FilterSection>
      )}
    </div>
  );

  //  Render 
  return (
    <div className="bg-gradient-to-b from-orange-50 to-white pb-10 px-4 md:px-10">
      <div className="w-full mx-auto py-4">
        {/* Top bar */}
        <div
          className="flex justify-between items-center mb-6"
          data-aos="fade-down"
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-philosopher text-[#002815]">
            {t("special_products") || "Special Products"}
          </h2>
          {/* Mobile filter toggle */}
          <button
            onClick={() => setMobileFilterOpen(true)}
            className="flex items-center gap-2 md:hidden bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium shadow-sm"
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
          </button>
        </div>

        <div className="flex gap-6">
          {/* ── Desktop Sidebar ── */}
          <aside className="hidden md:block w-52 flex-shrink-0">
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sticky top-24 max-h-[85vh] overflow-y-auto">
              <FilterPanel />
            </div>
          </aside>

          {/*  Mobile Drawer  */}
          {mobileFilterOpen && (
            <div className="fixed inset-0 z-50 flex md:hidden">
              <div
                className="flex-1 bg-black/40"
                onClick={() => setMobileFilterOpen(false)}
              />
              <div className="w-72 bg-white h-full overflow-y-auto p-5 shadow-2xl">
                <div className="flex justify-between items-center mb-4">
                  <span className="font-bold text-lg">Filters</span>
                  <button onClick={() => setMobileFilterOpen(false)}>
                    <X className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                <FilterPanel />
                <button
                  onClick={() => setMobileFilterOpen(false)}
                  className="mt-6 w-full bg-gray-900 text-white py-3 rounded-lg font-semibold"
                >
                  Show {filteredProducts.length} Results
                </button>
              </div>
            </div>
          )}

          {/*  Products Grid  */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-500 mb-4">
              Showing{" "}
              <span className="font-semibold text-gray-800">
                {filteredProducts.length}
              </span>{" "}
              of {products.length} products
            </p>

            {filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-gray-400">
                <SlidersHorizontal className="w-12 h-12 mb-3 opacity-30" />
                <p className="text-lg font-medium">
                  No products match your filters
                </p>
                <button
                  onClick={resetFilters}
                  className="mt-3 text-sm text-blue-600 hover:underline"
                >
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
                {filteredProducts.map((p, index) => {
                  const id = p.id?.toString() || p._id?.toString();
                  const isLiked = wishlist.includes(id);
                  const productName =
                    p.name ||
                    getLocalizedText(p, "name") ||
                    t("unnamed_product");

                  return (
                    <div
                      key={id}
                      onClick={() => handleProductClick(id)}
                      className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all cursor-pointer duration-300 relative"
                      data-aos="fade-up"
                      data-aos-delay={Math.min(index * 60, 400)}
                    >
                      {/* Wishlist */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleWishlist(id);
                        }}
                        className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 shadow-md hover:bg-gray-100"
                      >
                        {loadingHearts.has(id) ? (
                          <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                        ) : (
                          <Heart
                            className={`w-4 h-4 ${
                              isLiked
                                ? "fill-red-500 stroke-red-500"
                                : "stroke-gray-600"
                            }`}
                          />
                        )}
                      </button>

                      {/* Best badge */}
                      {p.isBestProduct && (
                        <span className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                          BEST
                        </span>
                      )}

                      {/* Image */}
                      <div className="bg-gray-50 overflow-hidden aspect-square">
                        <img
                          src={
                            p.image ||
                            p.images?.[0] ||
                            "https://via.placeholder.com/300x300?text=No+Image"
                          }
                          alt={productName}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          onError={(e) => {
                            e.target.src =
                              "https://via.placeholder.com/300x300?text=No+Image";
                          }}
                        />
                      </div>

                      {/* Info */}
                      <div className="p-3">
                        {renderStars(p.ratings || 0)}
                        <h3 className="text-xs sm:text-sm text-center mb-2 line-clamp-2 text-gray-800 font-medium leading-snug">
                          {productName}
                        </h3>
                        <div className="flex items-center justify-center gap-2">
                          {p.previous_price && (
                            <span className="text-xs text-gray-400 line-through">
                              {formatingPrice(
                                convertPrice(p.previous_price),
                                currency
                              )}
                            </span>
                          )}
                          <span className="text-sm font-bold text-gray-900">
                            {formatingPrice(convertPrice(p.price), currency)}
                          </span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddToCart(id);
                          }}
                          disabled={loadingCart.has(id)}
                          className="mt-3 w-full border border-gray-900 py-1.5 text-xs font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-colors duration-200"
                        >
                          {loadingCart.has(id) ? (
                            <span className="flex justify-center items-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" />{" "}
                              Adding...
                            </span>
                          ) : (
                            t("add_to_cart") || "ADD TO CART"
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialProducts;



// import React, { useEffect, useState, useMemo } from "react";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import {
//   Heart, Loader2, Star, SlidersHorizontal, X, ChevronDown, ChevronUp,
// } from "lucide-react";
// import { useNavigate } from "react-router-dom";
// import axios from "axios";
// import toast from "react-hot-toast";
// import { useCart } from "../../../utils/CartContext";
// import { useCurrency } from "../../../context/CurrencyContext";
// import { formatingPrice } from "../../../utils/formatingPrice";
// import { useTranslation } from "react-i18next";

// // ─── FilterSection accordion ───────────────────────────────────────────────
// const FilterSection = ({ title, children, defaultOpen = true }) => {
//   const [open, setOpen] = useState(defaultOpen);
//   return (
//     <div className="border-b border-gray-200 py-3">
//       <button
//         onClick={() => setOpen(!open)}
//         className="flex justify-between items-center w-full text-left"
//       >
//         <span className="font-semibold text-gray-800 text-sm">{title}</span>
//         {open
//           ? <ChevronUp className="w-4 h-4 text-gray-500" />
//           : <ChevronDown className="w-4 h-4 text-gray-500" />}
//       </button>
//       {open && <div className="mt-3">{children}</div>}
//     </div>
//   );
// };

// //  Main Component 
// const SpecialProducts = ({
//   products = [],
//   subcategories = [],
//   selectedSubCategoryFromParent,
//   onSubCategoryChange,
// }) => {
//   const { currency, convertPrice } = useCurrency();
//   const { t, i18n } = useTranslation();
//   const currentLanguage = i18n.language || "en";

//   const navigate = useNavigate();
//   const [wishlist, setWishlist] = useState([]);
//   const [loadingHearts, setLoadingHearts] = useState(new Set());
//   const [loadingCart, setLoadingCart] = useState(new Set());
//   const { fetchCartCount } = useCart();

//   // ── Filter state ──
//   const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
//   const [selectedSubCategory, setSelectedSubCategory] = useState(null);
//   const [selectedRatings, setSelectedRatings] = useState([]);
//   const [priceRange, setPriceRange] = useState([0, 100000]);
//   const [selectedMaterials, setSelectedMaterials] = useState([]);
//   const [selectedBrands, setSelectedBrands] = useState([]);
//   const [selectedHeights, setSelectedHeights] = useState([]);
//   const [selectedWeights, setSelectedWeights] = useState([]);
//   const [selectedMeasurements, setSelectedMeasurements] = useState([]);

//   const storedUser = localStorage.getItem("pm-user-data");
//   const user = storedUser ? JSON.parse(storedUser) : null;
//   const userId = user?._id;

//   useEffect(() => {
//     AOS.init({ duration: 800, once: true, offset: 100 });
//     if (userId) fetchWishlist();
//   }, [userId]);

  
//   useEffect(() => {
//     if (selectedSubCategoryFromParent !== undefined) {
//       setSelectedSubCategory(selectedSubCategoryFromParent);
//     }
//   }, [selectedSubCategoryFromParent]);

//   // ── DEFINE getLocalizedText FIRST before any useMemo that uses it ──
//   const getLocalizedText = (item, field) => {
//     if (!item || !item[field]) return "";
//     if (typeof item[field] === "string") return item[field];
//     if (typeof item[field] === "object") {
//       return (
//         item[field][currentLanguage] ||
//         item[field].en ||
//         item[field].hi ||
//         item[field].es ||
//         item[field].zh ||
//         ""
//       );
//     }
//     return "";
//   };

//   // ── Derive price bounds ──
//   const { minPrice, maxPrice } = useMemo(() => {
//     const prices = products.map((p) => p.price || 0);
//     return {
//       minPrice: prices.length ? Math.min(...prices) : 0,
//       maxPrice: prices.length ? Math.max(...prices) : 100000,
//     };
//   }, [products]);

//   // ── Init price range when products load ──
//   useEffect(() => {
//     setPriceRange([minPrice, maxPrice]);
//   }, [minPrice, maxPrice]);

//   // ── Derive all unique filter options from products ──
//   const allMaterials = useMemo(() => {
//     const set = new Set();
//     products.forEach((p) => {
//       // material is a multilingual object in your schema
//       const m = getLocalizedText(p, "material");
//       if (m) set.add(m);
//     });
//     return [...set];
//   // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [products, currentLanguage]);

//   const allBrands = useMemo(() => {
//     const set = new Set();
//     products.forEach((p) => {
//       if (p.brand) set.add(p.brand);
//     });
//     return [...set];
//   }, [products]);

//   const allHeights = useMemo(() => {
//     const set = new Set();
//     products.forEach((p) => {
//       if (p.height) set.add(p.height);
//     });
//     return [...set];
//   }, [products]);

//   const allWeights = useMemo(() => {
//     const set = new Set();
//     products.forEach((p) => {
//       if (p.weight) set.add(p.weight);
//     });
//     return [...set];
//   }, [products]);

//   const allMeasurements = useMemo(() => {
//     const set = new Set();
//     products.forEach((p) => {
//       const m = getLocalizedText(p, "measurement");
//       if (m) set.add(m);
//     });
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//     return [...set];
//   }, [products, currentLanguage]);

//   // ── Filtered products ──
//   const filteredProducts = useMemo(() => {
//     return products.filter((p) => {
//       // ── Subcategory filter ──
//       // products formatted in Category.jsx have subCategory as object with _id
//       if (selectedSubCategory) {
//         const subId =
//           p.subCategory?._id?.toString() ||
//           p.subCategoryId?.toString() ||
//           p.subCategory?.toString();
//         if (subId !== selectedSubCategory) return false;
//       }

//       // ── Rating filter ──
//       if (selectedRatings.length > 0) {
//         const minRating = Math.min(...selectedRatings);
//         if ((p.ratings || 0) < minRating) return false;
//       }

//       // ── Price filter (DO NOT touch language logic) ──
//       if (p.price < priceRange[0] || p.price > priceRange[1]) return false;

//       // ── Material filter ──
//       if (selectedMaterials.length > 0) {
//         const mat = getLocalizedText(p, "material");
//         if (!selectedMaterials.includes(mat)) return false;
//       }

//       // ── Brand filter ──
//       if (selectedBrands.length > 0) {
//         if (!selectedBrands.includes(p.brand)) return false;
//       }

//       // ── Height filter ──
//       if (selectedHeights.length > 0) {
//         if (!selectedHeights.includes(p.height)) return false;
//       }

//       // ── Weight filter ──
//       if (selectedWeights.length > 0) {
//         if (!selectedWeights.includes(p.weight)) return false;
//       }

//       // ── Measurement filter ──
//       if (selectedMeasurements.length > 0) {
//         const meas = getLocalizedText(p, "measurement");
//         if (!selectedMeasurements.includes(meas)) return false;
//       }

//       return true;
//     });

//   }, [
//     products,
//     selectedSubCategory,
//     selectedRatings,
//     priceRange,
//     selectedMaterials,
//     selectedBrands,
//     selectedHeights,
//     selectedWeights,
//     selectedMeasurements,
//   ]);

//   const toggleArray = (arr, setArr, val) =>
//     setArr(arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val]);

//   const handleSubCategorySelect = (id) => {
//     setSelectedSubCategory(id);
//     onSubCategoryChange?.(id); 
//   };

//   const resetFilters = () => {
//     setSelectedSubCategory(null);
//     setSelectedRatings([]);
//     setPriceRange([minPrice, maxPrice]);
//     setSelectedMaterials([]);
//     setSelectedBrands([]);
//     setSelectedHeights([]);
//     setSelectedWeights([]);
//     setSelectedMeasurements([]);
//     onSubCategoryChange?.(null);
//   };

//   // ── Wishlist ──
//   const fetchWishlist = async () => {
//     try {
//       const res = await axios.get(
//         `${process.env.REACT_APP_API_URL}/wishlist/wishlist-by-user/${userId}`
//       );
//       setWishlist(res?.data?.products?.map((p) => p._id?.toString()) || []);
//     } catch {}
//   };

//   const toggleWishlist = async (productId) => {
//     if (!userId)
//       return toast.error(t("login_for_wishlist") || "Please login to use wishlist ❤️");
//     const isLiked = wishlist.includes(productId);
//     setLoadingHearts((prev) => new Set(prev).add(productId));
//     try {
//       await axios.post(
//         isLiked
//           ? `${process.env.REACT_APP_API_URL}/wishlist/remove-from-wishlist`
//           : `${process.env.REACT_APP_API_URL}/wishlist/add-to-wishlist`,
//         { userId, productId }
//       );
//       await fetchWishlist();
//     } catch {
//       toast.error(t("something_went_wrong") || "Something went wrong!");
//     } finally {
//       setLoadingHearts((prev) => {
//         const s = new Set(prev);
//         s.delete(productId);
//         return s;
//       });
//     }
//   };

//   const handleAddToCart = async (productId) => {
//     if (!userId)
//       return toast.error(t("login_for_cart") || "Please login to add items to cart 🛒");
//     setLoadingCart((prev) => new Set(prev).add(productId));
//     try {
//       await axios.post(
//         `${process.env.REACT_APP_API_URL}/cart/add-product-to-cart`,
//         { userId, productId, quantity: 1 }
//       );
//       toast.success(t("added_to_cart") || "Added to cart!");
//       fetchCartCount();
//     } catch {
//       toast.error(t("add_to_cart_failed") || "Failed to add to cart");
//     } finally {
//       setLoadingCart((prev) => {
//         const s = new Set(prev);
//         s.delete(productId);
//         return s;
//       });
//     }
//   };

//   const handleProductClick = (id) => navigate(`/product-details/${id}`);

//   const renderStars = (rating) => (
//     <div className="flex justify-center gap-0.5 mb-1">
//       {[...Array(5)].map((_, i) => (
//         <Star
//           key={i}
//           className={`w-3.5 h-3.5 ${
//             i < Math.round(rating)
//               ? "text-yellow-400 fill-yellow-400"
//               : "text-gray-300"
//           }`}
//         />
//       ))}
//       {rating > 0 && (
//         <span className="text-xs text-gray-500 ml-1">{rating.toFixed(1)}</span>
//       )}
//     </div>
//   );

//   //  Sidebar Filter Panel 
//   const FilterPanel = () => (
//     <div className="w-full">
//       {/* Header */}
//       <div className="flex justify-between items-center mb-3">
//         <span className="font-bold text-gray-900 text-base">Filter</span>
//         <button
//           onClick={resetFilters}
//           className="text-xs text-blue-600 hover:underline flex items-center gap-1"
//         >
//           <X className="w-3 h-3" /> Reset
//         </button>
//       </div>

//       {/*  Subcategory  */}
//       {subcategories.length > 0 && (
//         <FilterSection title="Category">
//           <div className="space-y-2">
//             <label className="flex items-center gap-2 cursor-pointer text-sm">
//               <input
//                 type="radio"
//                 name="subcat"
//                 checked={!selectedSubCategory}
//                 onChange={() => handleSubCategorySelect(null)}
//                 className="accent-orange-500"
//               />
//               <span className="text-gray-700">All</span>
//             </label>
//             {subcategories.map((sub) => {
//               const name = getLocalizedText(sub, "name");
//               return (
//                 <label
//                   key={sub._id}
//                   className="flex items-center gap-2 cursor-pointer text-sm"
//                 >
//                   <input
//                     type="radio"
//                     name="subcat"
//                     checked={selectedSubCategory === sub._id}
//                     onChange={() => handleSubCategorySelect(sub._id)}
//                     className="accent-orange-500"
//                   />
//                   <span className="text-gray-700">{name}</span>
//                 </label>
//               );
//             })}
//           </div>
//         </FilterSection>
//       )}

//       {/*  Rating  */}
//       <FilterSection title="Rating">
//         <div className="space-y-2">
//           {[4, 3, 2, 1].map((r) => (
//             <label
//               key={r}
//               className="flex items-center gap-2 cursor-pointer text-sm"
//             >
//               <input
//                 type="checkbox"
//                 checked={selectedRatings.includes(r)}
//                 onChange={() =>
//                   toggleArray(selectedRatings, setSelectedRatings, r)
//                 }
//                 className="accent-orange-500 rounded"
//               />
//               <div className="flex items-center gap-0.5">
//                 {[...Array(5)].map((_, i) => (
//                   <Star
//                     key={i}
//                     className={`w-3 h-3 ${
//                       i < r
//                         ? "text-yellow-400 fill-yellow-400"
//                         : "text-gray-300"
//                     }`}
//                   />
//                 ))}
//                 <span className="text-gray-600 ml-1">{r}+</span>
//               </div>
//             </label>
//           ))}
//         </div>
//       </FilterSection>

//       {/*  Price Range  */}
//       <FilterSection title="Price">
//         <div className="space-y-3">
//           <div className="flex justify-between text-xs text-gray-600">
//             <span>₹{priceRange[0].toLocaleString()}</span>
//             <span>₹{priceRange[1].toLocaleString()}</span>
//           </div>
//           <input
//             type="range"
//             min={minPrice}
//             max={maxPrice}
//             step={1}
//             value={priceRange[0]}
//             onChange={(e) =>
//               setPriceRange([Number(e.target.value), priceRange[1]])
//             }
//             className="w-full accent-orange-500"
//           />
//           <input
//             type="range"
//             min={minPrice}
//             max={maxPrice}
//             step={1}
//             value={priceRange[1]}
//             onChange={(e) =>
//               setPriceRange([priceRange[0], Number(e.target.value)])
//             }
//             className="w-full accent-orange-500"
//           />
//         </div>
//       </FilterSection>

//       {/*  Brand  */}
//       {allBrands.length > 0 && (
//         <FilterSection title="Brand">
//           <div className="space-y-2">
//             {allBrands.map((b) => (
//               <label
//                 key={b}
//                 className="flex items-center gap-2 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={selectedBrands.includes(b)}
//                   onChange={() =>
//                     toggleArray(selectedBrands, setSelectedBrands, b)
//                   }
//                   className="accent-orange-500 rounded"
//                 />
//                 <span className="text-gray-700">{b}</span>
//               </label>
//             ))}
//           </div>
//         </FilterSection>
//       )}

//       {/*  Material  */}
//       {allMaterials.length > 0 && (
//         <FilterSection title="Material" defaultOpen={false}>
//           <div className="space-y-2">
//             {allMaterials.map((m) => (
//               <label
//                 key={m}
//                 className="flex items-center gap-2 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={selectedMaterials.includes(m)}
//                   onChange={() =>
//                     toggleArray(selectedMaterials, setSelectedMaterials, m)
//                   }
//                   className="accent-orange-500 rounded"
//                 />
//                 <span className="text-gray-700">{m}</span>
//               </label>
//             ))}
//           </div>
//         </FilterSection>
//       )}

//       {/*  Height  */}
//       {allHeights.length > 0 && (
//         <FilterSection title="Height" defaultOpen={false}>
//           <div className="space-y-2">
//             {allHeights.map((h) => (
//               <label
//                 key={h}
//                 className="flex items-center gap-2 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={selectedHeights.includes(h)}
//                   onChange={() =>
//                     toggleArray(selectedHeights, setSelectedHeights, h)
//                   }
//                   className="accent-orange-500 rounded"
//                 />
//                 <span className="text-gray-700">{h}</span>
//               </label>
//             ))}
//           </div>
//         </FilterSection>
//       )}

//       {/*  Weight  */}
//       {allWeights.length > 0 && (
//         <FilterSection title="Weight" defaultOpen={false}>
//           <div className="space-y-2">
//             {allWeights.map((w) => (
//               <label
//                 key={w}
//                 className="flex items-center gap-2 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={selectedWeights.includes(w)}
//                   onChange={() =>
//                     toggleArray(selectedWeights, setSelectedWeights, w)
//                   }
//                   className="accent-orange-500 rounded"
//                 />
//                 <span className="text-gray-700">{w}</span>
//               </label>
//             ))}
//           </div>
//         </FilterSection>
//       )}

//       {/*  Measurement  */}
//       {allMeasurements.length > 0 && (
//         <FilterSection title="Measurement" defaultOpen={false}>
//           <div className="space-y-2">
//             {allMeasurements.map((m) => (
//               <label
//                 key={m}
//                 className="flex items-center gap-2 cursor-pointer text-sm"
//               >
//                 <input
//                   type="checkbox"
//                   checked={selectedMeasurements.includes(m)}
//                   onChange={() =>
//                     toggleArray(
//                       selectedMeasurements,
//                       setSelectedMeasurements,
//                       m
//                     )
//                   }
//                   className="accent-orange-500 rounded"
//                 />
//                 <span className="text-gray-700">{m}</span>
//               </label>
//             ))}
//           </div>
//         </FilterSection>
//       )}
//     </div>
//   );

//   //  Render 
//   return (
//     <div className="bg-gradient-to-b from-orange-50 to-white pb-10 px-4 md:px-10">
//       <div className="w-full mx-auto py-4">
//         {/* Top bar */}
//         <div
//           className="flex justify-between items-center mb-6"
//           data-aos="fade-down"
//         >
//           <h2 className="text-2xl sm:text-3xl md:text-4xl font-philosopher text-[#002815]">
//             {t("special_products") || "Special Products"}
//           </h2>
//           {/* Mobile filter toggle */}
//           <button
//             onClick={() => setMobileFilterOpen(true)}
//             className="flex items-center gap-2 md:hidden bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium shadow-sm"
//           >
//             <SlidersHorizontal className="w-4 h-4" />
//             Filters
//           </button>
//         </div>

//         <div className="flex gap-6">
//           {/* ── Desktop Sidebar ── */}
//           <aside className="hidden md:block w-52 flex-shrink-0">
//             <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sticky top-24 max-h-[85vh] overflow-y-auto">
//               <FilterPanel />
//             </div>
//           </aside>

//           {/*  Mobile Drawer  */}
//           {mobileFilterOpen && (
//             <div className="fixed inset-0 z-50 flex md:hidden">
//               <div
//                 className="flex-1 bg-black/40"
//                 onClick={() => setMobileFilterOpen(false)}
//               />
//               <div className="w-72 bg-white h-full overflow-y-auto p-5 shadow-2xl">
//                 <div className="flex justify-between items-center mb-4">
//                   <span className="font-bold text-lg">Filters</span>
//                   <button onClick={() => setMobileFilterOpen(false)}>
//                     <X className="w-5 h-5 text-gray-600" />
//                   </button>
//                 </div>
//                 <FilterPanel />
//                 <button
//                   onClick={() => setMobileFilterOpen(false)}
//                   className="mt-6 w-full bg-gray-900 text-white py-3 rounded-lg font-semibold"
//                 >
//                   Show {filteredProducts.length} Results
//                 </button>
//               </div>
//             </div>
//           )}

//           {/*  Products Grid  */}
//           <div className="flex-1 min-w-0">
//             <p className="text-sm text-gray-500 mb-4">
//               Showing{" "}
//               <span className="font-semibold text-gray-800">
//                 {filteredProducts.length}
//               </span>{" "}
//               of {products.length} products
//             </p>

//             {filteredProducts.length === 0 ? (
//               <div className="flex flex-col items-center justify-center py-24 text-gray-400">
//                 <SlidersHorizontal className="w-12 h-12 mb-3 opacity-30" />
//                 <p className="text-lg font-medium">
//                   No products match your filters
//                 </p>
//                 <button
//                   onClick={resetFilters}
//                   className="mt-3 text-sm text-blue-600 hover:underline"
//                 >
//                   Clear filters
//                 </button>
//               </div>
//             ) : (
//               <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
//                 {filteredProducts.map((p, index) => {
//                   const id = p.id?.toString() || p._id?.toString();
//                   const isLiked = wishlist.includes(id);
//                   const productName =
//                     p.name ||
//                     getLocalizedText(p, "name") ||
//                     t("unnamed_product");

//                   return (
//                     <div
//                       key={id}
//                       onClick={() => handleProductClick(id)}
//                       className="group bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all cursor-pointer duration-300 relative"
//                       data-aos="fade-up"
//                       data-aos-delay={Math.min(index * 60, 400)}
//                     >
//                       {/* Wishlist */}
//                       <button
//                         onClick={(e) => {
//                           e.stopPropagation();
//                           toggleWishlist(id);
//                         }}
//                         className="absolute top-2 right-2 z-10 p-1.5 rounded-full bg-white/90 shadow-md hover:bg-gray-100"
//                       >
//                         {loadingHearts.has(id) ? (
//                           <Loader2 className="w-4 h-4 animate-spin text-red-500" />
//                         ) : (
//                           <Heart
//                             className={`w-4 h-4 ${
//                               isLiked
//                                 ? "fill-red-500 stroke-red-500"
//                                 : "stroke-gray-600"
//                             }`}
//                           />
//                         )}
//                       </button>

//                       {/* Best badge */}
//                       {p.isBestProduct && (
//                         <span className="absolute top-2 left-2 z-10 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
//                           BEST
//                         </span>
//                       )}

//                       {/* Image */}
//                       <div className="bg-gray-50 overflow-hidden aspect-square">
//                         <img
//                           src={
//                             p.image ||
//                             p.images?.[0] ||
//                             "https://via.placeholder.com/300x300?text=No+Image"
//                           }
//                           alt={productName}
//                           className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
//                           onError={(e) => {
//                             e.target.src =
//                               "https://via.placeholder.com/300x300?text=No+Image";
//                           }}
//                         />
//                       </div>

//                       {/* Info */}
//                       <div className="p-3">
//                         {renderStars(p.ratings || 0)}
//                         <h3 className="text-xs sm:text-sm text-center mb-2 line-clamp-2 text-gray-800 font-medium leading-snug">
//                           {productName}
//                         </h3>
//                         <div className="flex items-center justify-center gap-2">
//                           {p.previous_price && (
//                             <span className="text-xs text-gray-400 line-through">
//                               {formatingPrice(
//                                 convertPrice(p.previous_price),
//                                 currency
//                               )}
//                             </span>
//                           )}
//                           <span className="text-sm font-bold text-gray-900">
//                             {formatingPrice(convertPrice(p.price), currency)}
//                           </span>
//                         </div>
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation();
//                             handleAddToCart(id);
//                           }}
//                           disabled={loadingCart.has(id)}
//                           className="mt-3 w-full border border-gray-900 py-1.5 text-xs font-bold rounded-lg hover:bg-gray-900 hover:text-white transition-colors duration-200"
//                         >
//                           {loadingCart.has(id) ? (
//                             <span className="flex justify-center items-center gap-1">
//                               <Loader2 className="w-3 h-3 animate-spin" />{" "}
//                               Adding...
//                             </span>
//                           ) : (
//                             t("add_to_cart") || "ADD TO CART"
//                           )}
//                         </button>
//                       </div>
//                     </div>
//                   );
//                 })}
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SpecialProducts;